'use client';

import { useState } from 'react';
import { serviceEnquiryAPI } from '@/services/api';
import Footer from '@/components/Footer';
import Head from 'next/head';

const CarRentPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    from: '',
    to: '',
    travelDate: '',
    returnDate: '',
    carType: 'Microbus',
    passengers: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const data = await serviceEnquiryAPI.submitEnquiry({
        serviceType: 'car_rent',
        ...formData
      });

      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.message || 'Failed to submit');
      }
    } catch (err) {
      setError(err.message || 'Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Head>
        <title>Car Hire | Our Sherpur</title>
      </Head>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-lg mx-auto px-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-6 text-center">Hire a Car</h1>

          {success ? (
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <div className="text-green-600 text-5xl mb-4">&#10003;</div>
              <h2 className="text-xl font-semibold mb-2">Request Submitted!</h2>
              <p className="text-gray-600 mb-4">
                We have received your car hire request. Our team will contact you soon.
              </p>
              <button
                onClick={() => {
                  setSuccess(false);
                  setFormData({
                    name: '', phone: '', email: '', from: '', to: '',
                    travelDate: '', returnDate: '', carType: 'Microbus',
                    passengers: '', message: ''
                  });
                }}
                className="inline-block bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                Submit Another Request
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#16A34A]" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone No *</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#16A34A]" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From *</label>
                  <input type="text" name="from" value={formData.from} onChange={handleChange} required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#16A34A]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To *</label>
                  <input type="text" name="to" value={formData.to} onChange={handleChange} required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#16A34A]" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date to Go *</label>
                  <input type="date" name="travelDate" value={formData.travelDate} onChange={handleChange} required
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#16A34A]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date to Return</label>
                  <input type="date" name="returnDate" value={formData.returnDate} onChange={handleChange}
                    min={formData.travelDate || new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#16A34A]" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Car Type *</label>
                  <select name="carType" value={formData.carType} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#16A34A]">
                    <option value="Microbus">Microbus</option>
                    <option value="Sedan">Sedan</option>
                    <option value="SUV">SUV</option>
                    <option value="Pickup">Pickup</option>
                    <option value="CNG">CNG</option>
                    <option value="Truck">Truck</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Passengers</label>
                  <input type="number" name="passengers" value={formData.passengers} onChange={handleChange} min="1"
                    placeholder="How many?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#16A34A]" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#16A34A]" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea name="message" value={formData.message} onChange={handleChange} rows="3"
                  placeholder="Any additional information"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#16A34A]" />
              </div>

              {error && <p className="text-red-600 text-sm">{error}</p>}

              <button type="submit" disabled={submitting}
                className="w-full bg-[#16A34A] text-white py-3 px-4 rounded-lg font-medium hover:bg-[#128a3a] transition-colors disabled:opacity-50">
                {submitting ? 'Submitting...' : 'Submit Hire Request'}
              </button>
            </form>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CarRentPage;
